import type {
  SpareCandidate,
  SpareCandidateBucket,
  SpareCandidateEntry,
  SpareCandidateEntryBucket,
} from './types'

// Flatten one API entry into the flat SpareCandidate the UI consumes. `id` is
// the player id (row key + selection id + send payload value).
const toCandidate = (
  entry: SpareCandidateEntry,
  bucket: SpareCandidateBucket
): SpareCandidate => ({
  id: entry.player?.id,
  name_first: entry.player?.name_first,
  name_last: entry.player?.name_last,
  name_full: entry.player?.name_full,
  image: entry.player?.image,
  gender: entry.player?.gender ?? null,
  gender_id: entry.gender_id ?? entry.player?.gender_id ?? null,
  age: entry.player?.age ?? null,
  player_type_id:
    entry.player_type_id != null && entry.player_type_id !== ''
      ? Number(entry.player_type_id)
      : null,
  // Keep the name the API already sent, so the card doesn't depend on the
  // async Redux player-type list to show a position.
  player_type_name: entry.player_type_name ?? null,
  skill_level: entry.skill_level ?? null,
  bio: entry.bio ?? null,
  charge_rate: entry.charge_rate ?? null,
  available_days: entry.available_days ?? null,
  available_times: entry.available_times ?? null,
  bucket,
  source: entry.source,
  request: entry.request ?? null,
  is_available_on_game_day: entry.is_available_on_game_day ?? null,
  is_available_at_game_time: entry.is_available_at_game_time ?? null,
})

// The buckets the finder lists. `roster_spares` is left out: the team's own
// spares are already on the attendance screen.
const LISTED_BUCKETS: readonly SpareCandidateBucket[] = [
  'competition_spares',
  'public_finder',
]

const isListedBucket = (
  bucket: SpareCandidateEntryBucket
): bucket is SpareCandidateBucket =>
  (LISTED_BUCKETS as readonly string[]).includes(bucket)

/**
 * SN-8309 — one page of GET /games/:id/spare-candidates → the UI's rows: the
 * competition's spares and its public Player Finder profiles, in the server's
 * order, each carrying the `source` its ask is filed under. The server has
 * already applied the eligibility rules.
 */
export const toSpareCandidates = (
  entries?: readonly SpareCandidateEntry[] | null
): SpareCandidate[] =>
  (entries ?? []).flatMap((entry) =>
    isListedBucket(entry.bucket) ? [toCandidate(entry, entry.bucket)] : []
  )
